import type { OTCDealData } from '@src/types/OTC';
import { isFixedRateLoanDeal, isFloatingRateLoanDeal } from '@src/types/OTC/otcGuards';
import { formatProductType } from '@src/utils/OTC/otcUtils';
import {
  AssetRenderer,
  Box,
  Button,
  ButtonVariants,
  Card,
  FormControlSizes,
  HStack,
  Icon,
  IconName,
  logger,
  Text,
  useJsonModal,
  useMixpanel,
  useSecuritiesContext,
  VStack,
  type Security,
} from '@talos/kyoko';
import { useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { createDealJsonObject } from '../useCreateDealJsonObject';
import { InspectorText } from './InspectorText';

type OtcInspectorJsonPayload =
  | { deal: OTCDealData; security: Security | undefined }
  | { loan: OTCDealData; security: Security | undefined };

export const InspectorBasicInfo = ({ instrumentData }: { instrumentData: OTCDealData }) => {
  const theme = useTheme();
  const { handleClickJson, jsonModal } = useJsonModal<OtcInspectorJsonPayload>();
  const { securitiesBySymbol } = useSecuritiesContext();
  const mixpanel = useMixpanel();

  const resolvedSubProductTypeLabel = useMemo(() => {
    if (!instrumentData.Symbol) {
      logger.error(new Error('OTC instrument inspector: deal has no Symbol'));
      return formatProductType(undefined);
    }
    const dealSecurity = securitiesBySymbol.get(instrumentData.Symbol);
    if (!dealSecurity) {
      logger.error(new Error(`OTC instrument inspector: deal security not found for symbol ${instrumentData.Symbol}`), {
        extra: { symbol: instrumentData.Symbol },
      });
      return formatProductType(undefined);
    }
    return formatProductType(dealSecurity.SubProductType);
  }, [instrumentData.Symbol, securitiesBySymbol]);

  const onShowJson = useCallback(() => {
    const isLoan = isFixedRateLoanDeal(instrumentData) || isFloatingRateLoanDeal(instrumentData);
    createDealJsonObject({
      deal: instrumentData,
      propertyName: isLoan ? 'loan' : 'deal',
      securitiesBySymbol,
      handleClickJson,
      mixpanel,
    });
  }, [handleClickJson, instrumentData, mixpanel, securitiesBySymbol]);

  return (
    <Box data-testid="inspector-basic-info" p="spacing16" borderBottom={`1px solid ${theme.colors.gray['010']}`}>
      <VStack gap="spacing8">
        <HStack w="100%" justifyContent="flex-end">
          <Button variant={ButtonVariants.Default} size={FormControlSizes.Tiny} onClick={onShowJson}>
            <HStack color="gray.090" gap="3px">
              <Icon icon={IconName.Braces} />
              Show JSON
            </HStack>
          </Button>
        </HStack>
        <Card display="flex" flexDirection="column" w="100%">
          <VStack data-testid="inspector-basic-info-summary" gap="spacing8" w="100%" alignItems="stretch">
            <HStack justifyContent="space-between" alignItems="flex-start" gap="spacing12" w="100%">
              <Box flex={1} minWidth={0}>
                <AssetRenderer asset={instrumentData.Symbol} colorful />
              </Box>
              <Text
                color="colorTextImportant"
                data-testid="inspector-basic-info-sub-product-type"
                size="fontSizeMd"
                textAlign="right"
              >
                {resolvedSubProductTypeLabel}
              </Text>
            </HStack>
            <HStack justifyContent="space-between">
              <Text>Status</Text>
              <InspectorText>{instrumentData.Status}</InspectorText>
            </HStack>
          </VStack>
        </Card>
      </VStack>
      {jsonModal}
    </Box>
  );
};
